/**
 * Studiomate 연동 관련 React Query 훅
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";
import type {
  GetStudiomateStaffsResponse,
  MapInstructorRequest,
  MapInstructorResponse,
  UnmapInstructorRequest,
  UnmapInstructorResponse,
  UpdateInstructorEmailRequest,
  UpdateInstructorEmailResponse,
  DeleteInstructorRequest,
  DeleteInstructorResponse,
  SyncStudiomateMembersRequest,
  SyncStudiomateMembersResponse,
} from "@vlab-corporation/studiomate-api";
import type {
  CenterInstructorMappingsResponse,
  GetCenterInstructorMappingsRequest,
  RegisterInstructorAtCenterRequest,
  RegisterInstructorAtCenterResponse,
  DeregisterInstructorAtCenterRequest,
  DeregisterInstructorAtCenterResponse,
} from "@vlab-corporation/vlab-pilates-service-interface/instructor-mapping";

/**
 * Studiomate 강사 목록 조회 훅 (legacy)
 *
 * P1.5 이후 후보(staffs) 풀이 비어 미매핑 강사가 나타나지 않는다. 매핑 화면용으로는
 * useCenterInstructorMappings를 사용한다. 다른 화면(CenterDetailPage 등)은 이
 * 훅이 반환하는 instructors/owner/integration 정보를 그대로 사용한다.
 *
 * @param centerId - 조회할 센터 ID (필수).
 */
export function useStudiomateStaffs(centerId: number) {
  return useQuery({
    queryKey: ["studiomate", "staffs", centerId],
    queryFn: async () => {
      const fn = httpsCallable<{ centerId: number } | null, GetStudiomateStaffsResponse>(
        getFirebaseFunctions(),
        "getStudiomateStaffs",
      );
      const result = await fn({ centerId });
      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5분
  });
}

/**
 * 강사 매핑 화면용 데이터 조회 훅 (studiomate 비의존).
 *
 * vlab-checkin이 만든 centerMemberships에서 미매핑 후보(candidates)를 모아오고,
 * 센터 instructors의 매핑 상태와 owner 매핑을 함께 반환한다.
 *
 * @param centerId - 조회할 센터 ID (필수).
 */
export function useCenterInstructorMappings(centerId: number) {
  return useQuery({
    queryKey: ["center-instructor-mappings", centerId],
    queryFn: async () => {
      const fn = httpsCallable<
        GetCenterInstructorMappingsRequest,
        CenterInstructorMappingsResponse
      >(getFirebaseFunctions(), "getCenterInstructorMappings");
      const result = await fn({ centerId });
      return result.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * 강사 매핑 mutation
 */
export function useMapInstructor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: MapInstructorRequest & { staffName: string; centerId?: number },
    ) => {
      const fn = httpsCallable<
        MapInstructorRequest & { staffName: string; centerId?: number },
        MapInstructorResponse
      >(getFirebaseFunctions(), "mapInstructor");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * Admin 자신을 Studiomate 강사에 매핑하는 mutation
 */
export function useMapAdminAsInstructor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      studiomateStaffId: number;
      staffName: string;
      centerId?: number;
    }) => {
      const fn = httpsCallable<
        { studiomateStaffId: number; staffName: string; centerId?: number },
        MapInstructorResponse
      >(getFirebaseFunctions(), "mapAdminAsInstructor");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * 강사 매핑 해제 mutation
 */
export function useUnmapInstructor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UnmapInstructorRequest & { centerId?: number }) => {
      const fn = httpsCallable<
        UnmapInstructorRequest & { centerId?: number },
        UnmapInstructorResponse
      >(getFirebaseFunctions(), "unmapInstructor");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * 후보 강사를 센터에 등록하는 mutation.
 *
 * vlab-checkin 가입 강사(centerMembership에 등록된 후보)를 받아 중첩
 * instructor 문서를 생성하고 staffId와 매핑한다. 새 Firebase Auth 계정을
 * 만들지 않으므로 이메일·비밀번호 입력은 필요 없다.
 */
export function useRegisterInstructorAtCenter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RegisterInstructorAtCenterRequest) => {
      const fn = httpsCallable<
        RegisterInstructorAtCenterRequest,
        RegisterInstructorAtCenterResponse
      >(getFirebaseFunctions(), "registerInstructorAtCenter");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * 강사 등록취소 mutation (registerInstructorAtCenter의 역연산).
 *
 * instructor 중첩 문서를 제거하고 강사를 다시 미매핑 후보로 복귀시킨다.
 * UI의 Firebase 강사 목록 "해제" 버튼이 사용한다(owner 섹션은 unmapInstructor 유지).
 */
export function useDeregisterInstructor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeregisterInstructorAtCenterRequest) => {
      const fn = httpsCallable<
        DeregisterInstructorAtCenterRequest,
        DeregisterInstructorAtCenterResponse
      >(getFirebaseFunctions(), "deregisterInstructorAtCenter");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * 강사 삭제 mutation
 */
export function useDeleteInstructor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteInstructorRequest & { centerId?: number }) => {
      const fn = httpsCallable<
        DeleteInstructorRequest & { centerId?: number },
        DeleteInstructorResponse
      >(getFirebaseFunctions(), "deleteInstructor");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * 강사 이메일 변경 mutation
 */
export function useUpdateInstructorEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateInstructorEmailRequest & { centerId?: number }) => {
      const fn = httpsCallable<
        UpdateInstructorEmailRequest & { centerId?: number },
        UpdateInstructorEmailResponse
      >(getFirebaseFunctions(), "updateInstructorEmail");
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studiomate", "staffs"] });
      queryClient.invalidateQueries({ queryKey: ["center-instructor-mappings"] });
    },
  });
}

/**
 * Studiomate 회원 동기화 mutation
 */
export function useSyncStudiomateMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SyncStudiomateMembersRequest) => {
      const fn = httpsCallable<SyncStudiomateMembersRequest, SyncStudiomateMembersResponse>(
        getFirebaseFunctions(),
        "syncStudiomateMembers",
      );
      const result = await fn(request);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}
